import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  coreServicesApi,
  type DungeonSessionListParams,
  type DungeonEventParams,
  type DungeonCharacterListParams,
  type DungeonCharacterUpsert,
  type DungeonSessionListResponse,
  type DungeonSessionDetails,
  type DungeonSessionEventsResponse,
  type DungeonMetrics,
  type DungeonCharacterListResponse,
} from '../lib/api/core-services.js';

export const dungeonKeys = {
  all: ['dungeon'] as const,
  sessions: (params?: DungeonSessionListParams) => [...dungeonKeys.all, 'sessions', params || {}] as const,
  session: (sessionId: string) => [...dungeonKeys.all, 'session', sessionId] as const,
  events: (sessionId: string, params?: DungeonEventParams) => [...dungeonKeys.session(sessionId), 'events', params || {}] as const,
  metrics: () => [...dungeonKeys.all, 'metrics'] as const,
  characters: (params?: DungeonCharacterListParams) => [...dungeonKeys.all, 'characters', params || {}] as const,
  summarySettings: (guildId: string) => [...dungeonKeys.all, 'summary-settings', guildId] as const,
};

export const useDungeonSessions = (params?: DungeonSessionListParams) =>
  useQuery<DungeonSessionListResponse>({
    queryKey: dungeonKeys.sessions(params),
    queryFn: () => coreServicesApi.dungeonUi.listSessions(params),
  });

export const useDungeonSessionDetails = (sessionId?: string) => {
  return useQuery<DungeonSessionDetails>({
    queryKey: sessionId ? dungeonKeys.session(sessionId) : ['dungeon', 'session', 'missing'],
    queryFn: () => coreServicesApi.dungeonUi.getSessionDetails(sessionId!),
    enabled: Boolean(sessionId),
  });
};

export const useDungeonSessionEvents = (sessionId?: string, params?: DungeonEventParams) => {
  return useQuery<DungeonSessionEventsResponse>({
    queryKey: sessionId ? dungeonKeys.events(sessionId, params) : ['dungeon', 'events', 'missing'],
    queryFn: () => coreServicesApi.dungeonUi.getSessionEvents(sessionId!, params),
    enabled: Boolean(sessionId),
  });
};

export const useDungeonMetrics = () => {
  return useQuery<DungeonMetrics>({
    queryKey: dungeonKeys.metrics(),
    queryFn: () => coreServicesApi.dungeonUi.getMetrics(),
    refetchInterval: 30_000,
    staleTime: 30_000,
  });
};

export const useDungeonServerSettings = (guildId?: string) => {
  return useQuery<{ guildId: string; serverId: string; dungeonSettings: { allowedChannelId?: string | null } }>({
    queryKey: ['dungeon', 'server-settings', guildId ?? 'missing'],
    queryFn: () => coreServicesApi.dungeon.getServerSettings(guildId!),
    enabled: Boolean(guildId),
  });
};

export const useDungeonSummarySettings = (guildId?: string) =>
  useQuery<{ guildId: string | null; summaryRounds: number; source: 'guild' | 'global' | 'default' }>({
    queryKey: guildId ? dungeonKeys.summarySettings(guildId) : ['dungeon', 'summary-settings', 'missing'],
    queryFn: () => coreServicesApi.dungeon.getSummarySettings(guildId!),
    enabled: Boolean(guildId),
  });

export const useUpdateDungeonSummarySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ guildId, summaryRounds }: { guildId: string; summaryRounds: number }) =>
      coreServicesApi.dungeon.updateSummarySettings(guildId, { summaryRounds }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: dungeonKeys.summarySettings(variables.guildId) });
    },
  });
};

export const useDungeonCharacters = (params?: DungeonCharacterListParams, enabled: boolean = true) => {
  return useQuery<DungeonCharacterListResponse>({
    queryKey: dungeonKeys.characters(params),
    queryFn: () => coreServicesApi.dungeon.listCharacters(params),
    enabled,
  });
};

export const useDungeonCharacterMutations = () => {
  const queryClient = useQueryClient();

  const upsertCharacter = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: DungeonCharacterUpsert }) =>
      coreServicesApi.dungeon.upsertCharacter(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dungeon', 'characters'] });
    },
  });

  const deleteCharacter = useMutation({
    mutationFn: (characterId: string) => coreServicesApi.dungeon.deleteCharacter(characterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dungeon', 'characters'] });
    },
  });

  return {
    upsertCharacter,
    deleteCharacter,
    isLoading: upsertCharacter.isPending || deleteCharacter.isPending,
    error: upsertCharacter.error || deleteCharacter.error,
  };
};

export const useDungeonSessionMutations = () => {
  const queryClient = useQueryClient();

  const deleteSession = useMutation({
    mutationFn: (sessionId: string) => coreServicesApi.dungeonUi.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dungeonKeys.all, refetchType: 'all' });
    },
  });

  const previewBulkDeleteSessions = useMutation({
    mutationFn: (sessionIds: string[]) => coreServicesApi.dungeonUi.previewBulkDeleteSessions(sessionIds),
  });

  const bulkDeleteSessions = useMutation({
    mutationFn: (payload: { sessionIds: string[]; deleteDiscord?: boolean; forceUiDelete?: boolean }) =>
      coreServicesApi.dungeonUi.bulkDeleteSessions(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dungeonKeys.all, refetchType: 'all' });
    },
  });

  const cleanupOrphanedSessions = useMutation({
    mutationFn: () => coreServicesApi.dungeonUi.cleanupOrphanedSessions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dungeonKeys.all, refetchType: 'all' });
    },
  });

  return {
    deleteSession,
    previewBulkDeleteSessions,
    bulkDeleteSessions,
    cleanupOrphanedSessions,
    isLoading: deleteSession.isPending || cleanupOrphanedSessions.isPending || bulkDeleteSessions.isPending,
    error: deleteSession.error || cleanupOrphanedSessions.error || bulkDeleteSessions.error,
  };
};
